import type { Location } from 'react-router-dom';

/**
 * Where a user was headed when AuthGuard sent them to log in.
 *
 * React Router state does not survive the Cognito OAuth round trip, and Cognito
 * always returns to the site root, so the path is kept in sessionStorage: the
 * login page records it, and AuthGuard sends the user there once they are
 * authenticated.
 */
const STORAGE_KEY = 'choco-admin:post-login-redirect';

/**
 * Only same-origin paths are followed. `//host` and `/\host` are read by
 * browsers as another origin, which would make this an open redirect.
 */
const isSafeRedirectPath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

/**
 * Records the destination of the login attempt that starts now. A login with no
 * destination clears any left over from an earlier, abandoned attempt.
 */
export const rememberPostLoginRedirect = (from: Location | undefined): void => {
  if (!from?.pathname || from.pathname === '/login') {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(
    STORAGE_KEY,
    `${from.pathname}${from.search}${from.hash}`
  );
};

/** The recorded destination, or `null` when there is none or it is unsafe. */
export const readPostLoginRedirect = (): string | null => {
  const path = sessionStorage.getItem(STORAGE_KEY);
  return path && isSafeRedirectPath(path) ? path : null;
};

export const clearPostLoginRedirect = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};
